<template>
  <aside class="flex w-full max-w-sm shrink-0 flex-col border-r bg-muted/20 md:w-80">
    <div class="flex h-16 items-center justify-between border-b px-4">
      <div class="flex flex-col">
        <h1 class="text-lg font-semibold">
          Chat
        </h1>
        <span class="text-xs text-muted-foreground">{{ conversations.length }} conversations</span>
      </div>
      <div class="flex items-center gap-1">
        <Button variant="ghost" size="icon-sm" :disabled="!browserNotificationStore.canRequestPermission" :title="browserNotificationStore.statusLabel" :aria-label="browserNotificationStore.statusLabel" @click="browserNotificationStore.toggle">
          <BellRing v-if="browserNotificationStore.isEnabled" class="h-4 w-4" />
          <BellOff v-else-if="browserNotificationStore.permission === 'denied'" class="h-4 w-4" />
          <Bell v-else class="h-4 w-4" />
        </Button>

        <Button variant="ghost" size="icon-sm" :disabled="conversationStore.isLoading" @click="loadConversations">
          <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': conversationStore.isLoading }" />
        </Button>
      </div>
    </div>

    <UserStatusSelector />

    <ConversationCreatePanel :current-user-id="authStore.user?.id" />

    <ScrollArea class="min-h-0 flex-1">
      <Alert v-if="conversationListError" variant="destructive" class="m-3">
        <AlertDescription>{{ conversationListError }}</AlertDescription>
      </Alert>

      <div v-if="conversationStore.isLoading && conversations.length === 0" class="flex flex-col gap-2 p-3">
        <Skeleton v-for="index in 6" :key="index" class="h-16 w-full rounded-md" />
      </div>

      <div v-else-if="conversations.length === 0" class="p-4 text-sm text-muted-foreground">
        No conversations yet.
      </div>

      <div v-else class="flex flex-col gap-1 p-2">
        <router-link
          v-for="conversation in conversations"
          :key="conversation.id"
          :to="{ name: 'ChatShowPage', params: { conversation: conversation.id } }"
          class="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent"
          :class="activeConversationId === Number(conversation.id) ? 'bg-accent text-accent-foreground' : ''"
        >
          <Avatar size="sm">
            <AvatarImage v-if="conversation.avatar_path" :src="conversation.avatar_path" />
            <AvatarFallback>{{ conversationInitials(conversation) }}</AvatarFallback>
          </Avatar>

          <span class="min-w-0 flex-1">
            <span class="flex items-center justify-between gap-2">
              <span class="truncate text-sm font-medium">{{ conversationTitle(conversation) }}</span>
              <span class="shrink-0 text-[11px] text-muted-foreground">{{ formatTime(conversation.last_message_at) }}</span>
            </span>
            <span class="mt-0.5 flex items-center justify-between gap-2">
              <span class="truncate text-xs text-muted-foreground">{{ lastMessagePreview(conversation) }}</span>
              <Badge v-if="conversation.current_participant?.unread_count > 0" variant="secondary" class="shrink-0 px-1.5 py-0 text-[10px]">
                {{ conversation.current_participant.unread_count }}
              </Badge>
            </span>
          </span>
        </router-link>
      </div>
    </ScrollArea>
  </aside>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Bell, BellOff, BellRing, RefreshCw } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { ScrollArea } from '../../../../../../../resources/js/components/ui/scroll-area/index.js'
import { Skeleton } from '../../../../../../../resources/js/components/ui/skeleton/index.js'
import { useAuthStore } from '../../../../../../../resources/js/stores/auth.js'
import { useBrowserNotificationStore } from '../../stores/browserNotificationStore.js'
import { useChatStore } from '../../stores/chatStore.js'
import { useConversationStore } from '../../stores/conversationStore.js'
import ConversationCreatePanel from './ConversationCreatePanel.vue'
import UserStatusSelector from '../users/UserStatusSelector.vue'

const route = useRoute()
const authStore = useAuthStore()
const chatStore = useChatStore()
const conversationStore = useConversationStore()
const browserNotificationStore = useBrowserNotificationStore()

const { items: conversations } = storeToRefs(conversationStore)

const conversationListError = ref('')

const activeConversationId = computed(() => {
  const conversationId = Number(route.params.conversation)

  return Number.isInteger(conversationId) && conversationId > 0 ? conversationId : null
})

const loadConversations = async () => {
  conversationListError.value = ''

  try {
    await conversationStore.fetchConversations({
      clear: true,
      per_page: 50,
    })
    chatStore.subscribeToConversations(conversations.value.map((conversation) => conversation.id))
  } catch (error) {
    conversationListError.value = errorMessageFrom(error, 'Unable to load conversations.')
  }
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

const conversationTitle = (conversation) => {
  if (conversation.title) {
    return conversation.title
  }

  const otherParticipant = conversation.participants?.find((participant) => participant.user_id !== authStore.user?.id)

  return otherParticipant?.user?.name || 'Direct conversation'
}

const conversationInitials = (conversation) => {
  return conversationTitle(conversation)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'C'
}

const lastMessagePreview = (conversation) => {
  return conversation.last_message?.body || conversation.last_message?.type || 'No messages yet'
}

const formatTime = (value) => {
  if (!value) {
    return ''
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

onMounted(() => {
  browserNotificationStore.initialize()
  chatStore.subscribeToGlobalPresence()
  loadConversations()
})

</script>
